import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, FetchAPI, FieldAPI, FieldListAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBeforeSubmit, OnCancel, OnChange, OnEdit, OnFocus, OnMount, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpResponse } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { InsurancesFormAliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { LocaleTypes } from '../../constants/locale-types';
import ProfileModalUploadService from '../../services/profile-modal-upload-service';
import { showToastNotificationError, showToastNotificationSuccess } from '../../utils/toast-notification-utils';
import UserContext from '../../utils/user-context';
import ProfileBaseController from '../profile-base-controller';
import { AutocompleteInsuranceType } from './autocomplete-insurance-types';
import './index.css';
import { PatientHttpService } from '../../services/patient-http-service';

interface MyInsurancesProps {
  insurances: InsuranceProps[];
}

interface InsuranceProps {
  categoriaObj: CategoryObjProps | Record<string, unknown>;
  cdCategoria: string;
  cdConvenio: number;
  cdPlano: string;
  cdPlanoConvenio: string;
  cdUsuarioConvenio: string;
  convenioObj: HealthInsuranceObjProps | Record<string, unknown>;
  dsCategoria: string;
  dsConvenio: string;
  dsPlanConvenio: string;
  dtFimVigencia: string;
  dtInicioVigencia: string;
  dtValidadeCarteira: string;
  nrSequencia: number;
  planoObj: PlanObjProps | Record<string, unknown>;
}

interface AutocompleteCategoryValues {
  cdCategoria: string;
  cdConvenio: number;
}

interface AutocompleteHealthInsuranceValues {
  cdConvenio: number;
  dsConvenio: string;
}

interface State {
  oldFormValues: Record<string, ValueRecord>[];
}

interface FieldTypes {
  dsValorNew: string | number | ValueRecord | null;
  dsValorOld: string | number | ValueRecord | null;
  nmAtributo: string;
  nrSequencia: number;
}

interface ValueRecord {
  cdCategoria: string | undefined;
  cdConvenio: number;
  cdPlanoConvenio: string | undefined;
  cdUsuarioConvenio: string;
  dsCategoria: string;
  dsConvenio: string;
  dsPlanConvenio: string;
  dtFimVigencia: string;
  dtInicioVigencia: string;
  dtValidadeCarteira: string;
  nrSequencia: number;
}

interface HealthInsuranceObjProps {
  cdConvenio: number;
  dsConvenio: string;
}

interface CategoryObjProps {
  cdCategoria: string;
  cdConvenio: number;
  dsCategoria: string;
}

interface PlanObjProps {
  cdConvenio: number;
  cdPlano: string;
  dsPlano: string;
}

export interface HealthInsuranceResponse {
  message: string;
}

interface FieldsRequireProof {
  dsDocumento: string;
  ieAttributo: string;
  nrSequenciaDoc: number;
}

@Controller({ fragment: 'insurances-form' })
export default class InsuranceFormController extends ProfileBaseController {

  @Inject('form-insurances')
    formInsurances!: FormAPI;

  @Inject('insurances-field-list')
    insurancesFieldList!: FieldListAPI;

  autocompleteCategoryList: AutocompleteAPI[] = [];
  autocompletePlanList: AutocompleteAPI[] = [];
  autocompleteInsuranceList: AutocompleteAPI[] = [];
  fetchCategoryList: FetchAPI[] = [];
  fetchPlanList: FetchAPI[] = [];

  state: State = { oldFormValues: [{}] };

  @OnMount(InsurancesFormAliases.FORM_INSURANCES)
  onMountFormInsurances(event: MountEvent): void {
    const formApi = event.target as FormAPI;
    super.setContextVariablesForTaskModule();
    super.listenWhetherProfileEditingIsEnabled(formApi);
  }

  @OnMount({ target: 'layout-0' })
  async onMountLayout(): Promise<void> {
    ToastService.closeAll();
    await this.fetchDataAndShow();
  }

  @OnUnmount({target: 'layout-0'})
  onUnmountLayout(): void {
    ToastService.closeAll();
  }

  async fetchDataAndShow(): Promise<void> {
    try {
      this.formInsurances.setLoading(true);
      const response = await PatientHttpService.get(APPLICATION_ENDPOINTS.MY_INSURANCES, { personCode: UserContext.patientId, });
      const body = response.body as MyInsurancesProps;
      const data = this.createAutocompleteValues(body.insurances);
      this.formInsurances.setData(data);
    } catch (error) {
      showToastNotificationError(MessageService.translate('tws.toast-notification.load-data-error'));
    } finally {
      this.formInsurances.setLoading(false);
    }
  }

  @OnMount({ target: ['field-wallet-expiry-date'] })
  onMountWalletField(event: MountEvent): void {
    const language = LanguageService.getCurrentLanguage();
    const currentWalletFiel = event.target as FieldAPI;

    if (language === LocaleTypes.AUSTRALIA){
      currentWalletFiel.setVisible(false);
    }
  }

  @OnMount({ target: ['autocomplete-category', 'autocomplete-plan', 'autocomplete-insurance'] })
  onMountAutocomplete(event: MountEvent): void {
    const currentAutocomplete = event.target as AutocompleteAPI;
    //@ts-ignore
    const index = currentAutocomplete.state.identifier as number;
    const alias = currentAutocomplete.getAlias();
    switch (alias) {
      case 'autocomplete-category':
        this.autocompleteCategoryList[index] = currentAutocomplete;
        break;
      case 'autocomplete-plan':
        this.autocompletePlanList[index] = currentAutocomplete;
        break;
      case 'autocomplete-insurance':
        this.autocompleteInsuranceList[index] = currentAutocomplete;
        break;
    }
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnMount({ target: ['fetch-category', 'fetch-plan'] })
  onMountFetch(event: MountEvent): void {
    const currentFetch = event.target as FetchAPI;
    //@ts-ignore
    const index = currentFetch.state.identifier as number;
    const alias = currentFetch.getAlias();
    switch (alias) {
      case 'fetch-category':
        this.fetchCategoryList[index] = currentFetch;
        break;
      case 'fetch-plan':
        this.fetchPlanList[index] = currentFetch;
        break;
    }
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnFocus({ target: ['autocomplete-category', 'autocomplete-plan'] })
  onFocusAutocomplete(event: FocusEvent): void {
    const currentAutocomplete = event.target as unknown as AutocompleteAPI;
    //@ts-ignore
    const index = currentAutocomplete.state.identifier as number;
    const alias = currentAutocomplete.getAlias();
    if (!currentAutocomplete.getData()) {
      const { cdConvenio } = this.autocompleteInsuranceList[index].getValue() as unknown as AutocompleteHealthInsuranceValues;
      const autocompleteCategoryValues = this.autocompleteCategoryList[index].getValue() as unknown as AutocompleteCategoryValues;
      const cdCategoria = autocompleteCategoryValues ? autocompleteCategoryValues.cdCategoria : undefined;
      switch (alias) {
        case 'autocomplete-category':
          this.fetchCategoryList[index].setParameters({ cdConvenio });
          this.fetchCategoryList[index].forceFetch();
          break;
        case 'autocomplete-plan':
          this.showInsurancePlan(this.fetchPlanList[index], cdConvenio?.toString() ?? '', cdCategoria?.toString() ?? '');
          break;
      }
    }
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnChange({ target: ['autocomplete-category', 'autocomplete-insurance', 'autocomplete-plan'] })
  onChangeAutoComplete(event: ChangeEvent<Record<string, Value>>): void {
    const currentAutocomplete = event.target as AutocompleteAPI;
    const alias = currentAutocomplete.getAlias() as string;
    //@ts-ignore
    const index = currentAutocomplete.state.identifier as number;
    const insuranceIndex = Object.keys(this.autocompleteInsuranceList).indexOf(index.toString()) as number;
    if(event.value) {
      const { cdConvenio, cdCategoria, cdPlano } = event.value;
      switch (alias) {
        case 'autocomplete-insurance':
          this.autocompleteCategoryList[index].setValue([]);
          this.autocompletePlanList[index].setValue([]);
          this.updateAutocompleteValuesOnChange(AutocompleteInsuranceType.INSURANCE, insuranceIndex, cdConvenio);
          this.fetchCategoryList[index].setParameters({ cdConvenio });
          this.fetchCategoryList[index].forceFetch();
          this.showInsurancePlan(this.fetchPlanList[index], cdConvenio?.toString() ?? '', '');
          break;
        case 'autocomplete-category':
          this.autocompletePlanList[index].setValue([]);
          this.updateAutocompleteValuesOnChange(AutocompleteInsuranceType.CATEGORY, insuranceIndex, cdCategoria);
          this.showInsurancePlan(this.fetchPlanList[index], cdConvenio?.toString() ?? '', cdCategoria?.toString() ?? '');
          break;
        case 'autocomplete-plan':
          this.updateAutocompleteValuesOnChange(AutocompleteInsuranceType.PLAN, insuranceIndex, cdPlano);
          break;
      }
    } else {
      this.updateAutocompleteValuesOnClear(alias, index);
    }
  }

  // TODO: V6 REVALIDATE
  // WARNING:'TS-IGNORE' TEMPORARILY ADDED PER TEC GUIDANCE.
  // THE CODE SHOULD BE ADJUSTED BY THE DEV WITH THE NEXT FIX/FEATURE
  // VERSION USING THE DEPENDSON PROPERTY OF THE FIELD COMPONENT.
  @OnAfterFetch({ target: ['fetch-plan', 'fetch-category'] })
  onAfterFetch(event: FetchEvent): void {
    const data = event.data as [];
    const isEnable = data ? data.length != 0 : false;
    const currentFetch = event.target as FetchAPI;
    const dsCategoria = currentFetch.getParameters()?.dsCategoria;
    const dsPlano = currentFetch.getParameters()?.dsPlano;
    const alias = currentFetch.getAlias();
    //@ts-ignore
    const index = currentFetch.state.identifier as number;
    switch (alias) {
      case 'fetch-plan':
        !dsPlano && this.autocompletePlanList[index].setEnabled(isEnable);
        break;
      case 'fetch-category':
        !dsCategoria && this.autocompleteCategoryList[index].setEnabled(isEnable);
        !dsCategoria && this.autocompletePlanList[index].setEnabled(isEnable);
        break;
    }
  }

  /**
   * - Autocomplete objects: categoriaObj, convenioObj and planoObj.
   * @param insurances
   * @returns an object with an array of insurances with values filled in for each autocomplete object.
   */
  createAutocompleteValues(insurances: InsuranceProps[]): Record<string, Value> {
    const size = insurances.length - 1;
    for (let i = size; i >= 0; i--) {
      const { cdCategoria, dsCategoria, cdConvenio, dsConvenio, cdPlano, dsPlanConvenio } = insurances[i];
      insurances[i].convenioObj = cdConvenio ? { cdConvenio, dsConvenio } : {};
      insurances[i].categoriaObj = cdCategoria && cdConvenio ? { cdCategoria, cdConvenio, dsCategoria } : {};
      insurances[i].planoObj = cdPlano && cdConvenio && cdCategoria ? { cdCategoria, cdConvenio, cdPlano, dsPlano: dsPlanConvenio } : {};
      /**
       * It was necessary to create a new 'name' for the cdPlano attribute,
       * because when changing the record, the name of the field in the table is checked.
       */
      insurances[i].cdPlanoConvenio = cdPlano;
    }

    return { insurances };
  }

  @OnSubmit({ target: 'form-insurances' })
  onCancelAndSubmitForm(): void {
    this.insurancesFieldList.setReadOnly(true);
  }

  @OnCancel({ target: 'form-insurances' })
  onCancelForm(): void {
    this.insurancesFieldList.setReadOnly(true);
    this.insurancesFieldList.setValue(this.state.oldFormValues);
  }

  @OnEdit({ target: 'form-insurances' })
  onEditForm(): void {
    this.state.oldFormValues = this.cloneObjectByJson(this.insurancesFieldList.getValue()) as unknown as Record<string, ValueRecord>[];
    this.insurancesFieldList.setReadOnly(false);
  }

  /**
   * Created specifically for this controller.\
   * Warning: Do not use for large objects.\
   * Do not use in cases:
   * - If the object has a function;
   * - If the object has dates and time.
   * @param object
   * @returns a copy of the same object.
   */
  cloneObjectByJson<T>(object: T): T {
    const rawObject = JSON.stringify(object);
    return JSON.parse(rawObject);
  }

  /**
   * @param autocompleteType AutocompleteInsuranceType
   * @param index FieldList item index.
   * @param newValue The value received from the autocomplete on onchange.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
  updateAutocompleteValuesOnChange(autocompleteType: AutocompleteInsuranceType, index: number, newValue: any): void {
    const currentFieldListItem = this.insurancesFieldList.getValue()[index] as unknown as ValueRecord;
    switch (autocompleteType) {
      case AutocompleteInsuranceType.CATEGORY:
        currentFieldListItem.cdCategoria = newValue;
        currentFieldListItem.cdPlanoConvenio = undefined;
        break;
      case AutocompleteInsuranceType.PLAN:
        currentFieldListItem.cdPlanoConvenio = newValue;
        break;
      case AutocompleteInsuranceType.INSURANCE:
        currentFieldListItem.cdConvenio = newValue;
        currentFieldListItem.cdCategoria = undefined;
        currentFieldListItem.cdPlanoConvenio = undefined;
        break;
      default:
        throw new Error(`The type ${autocompleteType} does not exist in AutocompleteInsuranceType.`);
    }
  }

  /**
   * Clear the autocomplete value.
   * @param autocompleteAlias Autocomplete component alias.
   * @param index The current index of the autocomplete component.
   */
  updateAutocompleteValuesOnClear(autocompleteAlias: string, index: number): void {
    switch (autocompleteAlias) {
      case 'autocomplete-insurance':
        this.updateAutocompleteValuesOnChange(AutocompleteInsuranceType.INSURANCE, index, undefined);
        break;
      case 'autocomplete-category':
        this.updateAutocompleteValuesOnChange(AutocompleteInsuranceType.CATEGORY, index, undefined);
        break;
      case 'autocomplete-plan':
        this.updateAutocompleteValuesOnChange(AutocompleteInsuranceType.PLAN, index, undefined);
        break;
      default:
        throw new Error(`The alias value ${autocompleteAlias} does not exist in updateFieldListItemOnClear`);
    }
  }

  @OnBeforeSubmit({ target: 'form-insurances' })
  async onBeforeSubmitForm(): Promise<void> {
    this.formInsurances.setLoading(true);
    const newFormValues = this.insurancesFieldList.getValue() as unknown as Record<string, ValueRecord>[];
    const oldFormValues = this.state.oldFormValues;

    if (newFormValues.length > oldFormValues.length) {
      await this.requestCreateHealthInsurance(oldFormValues, newFormValues);
    }
    await this.requestUpdateHealthInsurance(oldFormValues, newFormValues);
    await this.fetchDataAndShow();
  }

  async requestCreateHealthInsurance(oldFormValues: Record<string, ValueRecord>[], newFormValues: Record<string, ValueRecord>[]): Promise<void> {
    const insertPointIndex = oldFormValues.length;
    const insertItems = newFormValues.filter((item, index) => !item.nrSequencia && index >= insertPointIndex);
    const body = insertItems as unknown as Record<string, Value>;

    await HttpService.post(APPLICATION_ENDPOINTS.POST_PROFILE_CREATE_INSURANCE, {patientId: UserContext.patientId}, { body })
      .then(() => showToastNotificationSuccess(MessageService.translate('insurance.new-insurance-added')))
      .catch((error) => showToastNotificationError(error?.message));
  }

  async requestUpdateHealthInsurance(oldFormValues: Record<string, ValueRecord>[],
    newFormValues: Record<string, ValueRecord>[]): Promise<void> {
    const campos: FieldTypes[] = [];
    const body = { campos };
    const updatedFields: string[] = [];
    const oldFormSize = oldFormValues.length - 1;
    for (let i = oldFormSize; i >= 0; i--) {
      const currentFieldListItem = newFormValues[i] as unknown as ValueRecord;

      Object.keys(newFormValues[i]).filter(key => {
        return key !== AutocompleteInsuranceType.CATEGORY
          && key !== AutocompleteInsuranceType.INSURANCE
          && key !== AutocompleteInsuranceType.PLAN
      }).forEach(key => {
        const oldValues = oldFormValues[i];
        const newValues = newFormValues[i];
        if (oldValues[key] !== newValues[key]) {
          updatedFields.push(key.toUpperCase());
          const oldV = oldValues[key];
          const newV = newValues[key];
          body.campos.push({
            dsValorNew: newV
              ? newV.cdConvenio
              || newV.cdCategoria
              || newV.cdPlanoConvenio
              || newV.dtInicioVigencia
              || newV.dtFimVigencia
              || newV.cdUsuarioConvenio
              || newV.dtValidadeCarteira
              || newV : null,
            dsValorOld: oldV
              ? oldV.cdConvenio
              || oldV.cdCategoria
              || oldV.cdPlanoConvenio
              || oldV.dtInicioVigencia
              || oldV.dtFimVigencia
              || oldV.cdUsuarioConvenio
              || oldV.dtValidadeCarteira
              || oldV : null,
            nmAtributo: key,
            nrSequencia: currentFieldListItem.nrSequencia,
          });
        }
      });
    }
    if (body.campos.length > 0) {
      HttpService.get(APPLICATION_ENDPOINTS.GET_FIELDS_REQUIRE_PROOF).then((resp: HttpResponse<unknown>) => {
        const { body: bodyResponse } = resp as HttpResponse<FieldsRequireProof[]>;
        const fieldsRequireProof = bodyResponse && bodyResponse
          .filter(doc => updatedFields.some((updatedField) => updatedField === doc.ieAttributo.replace(/_/g, '')))
          .map(doc => ({
            attribute: doc.ieAttributo,
            key: doc.nrSequenciaDoc.toString(),
            required: true,
            value: doc.dsDocumento,
          }));
        if (fieldsRequireProof && fieldsRequireProof.length > 0) {
          ProfileModalUploadService.open({
            bodyToUpdate: body,
            mandatoryAttachmentsFields: fieldsRequireProof,
            onConfirmUpload: e => this.sendUpdateRequest(e.record),
          });
        } else {
          this.sendUpdateRequest(body);
        }
      });
    }
  }

  private sendUpdateRequest(body: Record<string, Value>): void {
    HttpService.post(APPLICATION_ENDPOINTS.POST_PROFILE_UPDATE_INSURANCE, { patientCode: UserContext.patientId }, { body })
      .then(response => {
        const healthInsuranceResponse = response?.body as HealthInsuranceResponse;
        showToastNotificationSuccess(healthInsuranceResponse?.message);
      })
      .catch(error => showToastNotificationError(error?.message));
  }

  showInsurancePlan(fieldFetch: FetchAPI, cdConvenio: string, categoryCode: string): void {
    if (cdConvenio) {
      setTimeout(() => {
        const validateRules = false;
        fieldFetch.setParameters({ categoryCode, cdConvenio, validateRules });
        fieldFetch.forceFetch();
      });
    }
  }

}
